/**
 * Feature Flag Library for SellPath UI
 * ------------------------------------
 * Loads feature flags from /feature-flags.json in the public directory at runtime.
 * Flags are cached in memory for performance. Supports reload for dynamic updates.
 *
 * Usage:
 *   import { isFeatureEnabled, useFeatureFlag, reloadFeatureFlags } from "@/utils/featureFlags";
 *   if (isFeatureEnabled("showSalesEscalation")) { ... }
 *   const enabled = useFeatureFlag("showSalesEscalation");
 * default config file
 * ui/public/feature-flags.json
 */
import { useEffect, useState } from "react";

// Internal cache for feature flags
let featureFlags: Record<string, boolean> = {};
let loaded = false;

// Load flags from public/feature-flags.json
async function loadFeatureFlags(): Promise<void> {
  try {
    // Add cache-busting param in dev, rely on cache headers in prod
    const url = process.env.NODE_ENV === "development" ? `/feature-flags.json?ts=${Date.now()}` : "/feature-flags.json";
    const res = await fetch(url, { cache: "reload" });
    if (res.ok) {
      featureFlags = await res.json();
      loaded = true;
    } else {
      featureFlags = {};
      loaded = true;
    }
  } catch {
    featureFlags = {};
    loaded = true;
  }
}

// Synchronous getter (returns false if not loaded yet)
export function isFeatureEnabled(flagName: string): boolean {
  return !!featureFlags[flagName];
}

// React hook for feature flag usage in components
export function useFeatureFlag(flagName: string): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flagName));

  useEffect(() => {
    if (!loaded) {
      loadFeatureFlags().then(() => {
        setEnabled(isFeatureEnabled(flagName));
      });
    } else {
      setEnabled(isFeatureEnabled(flagName));
    }
  }, [flagName]);

  return enabled;
}

// Manual reload (for admin/ops or hot updates)
export async function reloadFeatureFlags(): Promise<void> {
  await loadFeatureFlags();
}

// Export raw flags for debug modal
export { featureFlags };
